import React, { useMemo } from "react";
import { useS3Filter } from "../../contexts/S3FilterContext";
import S3FileBrowser from "./S3FileBrowser";
import { S3FilterPanel } from "./S3FilterPanel";
import { Paper, Box, Divider } from "@mui/material";
import { themeColors } from "../../utils/theme";

/**
 * Props for FilteredS3Browser
 */
export interface FilteredS3BrowserProps {
  /** Base path for S3 browser */
  basePath?: string;
}

/**
 * FilteredS3Browser Component
 *
 * Integration layer that connects the filter UI with S3FileBrowser.
 *
 * This component:
 * - Constructs the S3 path/prefix based on partner/year/month filters
 * - Appends search text to the path as an S3 prefix for direct S3 filtering
 * - Preserves folder navigation functionality
 * - Works with S3's native prefix-based filtering (no client-side filtering)
 *
 * Example paths generated:
 * - No filters: "" (root)
 * - Partner only: "Exports/"
 * - Partner + Year: "Exports/2025/"
 * - Partner + Year + Month: "Exports/2025/January/"
 * - With search "abc": "Exports/2025/January/abc" (S3 returns items starting with "abc")
 */
export const FilteredS3Browser: React.FC<FilteredS3BrowserProps> = ({
  basePath = "Exports/",
}) => {
  // Get filter state from context
  const { filters } = useS3Filter();

  /**
   * Construct the initial path based on applied filters
   * Structure: basePath + partner/ + year/ + month/ + searchText (prefix)
   * Note: Month names are capitalized in S3 (e.g., "October")
   */
  const getFilteredPath = (): string => {
    let path = basePath;

    // Add partner to path if single partner selected
    if (filters.selectedPartners.length === 1) {
      path += `${filters.selectedPartners[0]}/`;
    }

    // Add year to path if selected
    if (filters.selectedYear) {
      path += `${filters.selectedYear}/`;
    }

    // Add month to path if single month selected
    // Month names are stored capitalized (e.g., "October")
    if (filters.selectedMonths.length === 1) {
      path += `${filters.selectedMonths[0]}/`;
    }

    // Add search text as a prefix for S3 filtering
    // S3 will return only items that start with this prefix
    if (filters.searchText && filters.searchText.trim()) {
      path += filters.searchText.trim();
    }

    return path;
  };

  const filteredPath = getFilteredPath();

  /**
   * Generate a unique key that includes search text
   * This forces browser remount when path OR search changes
   */
  const browserKey = useMemo(() => {
    return `${filteredPath}-${filters.searchText}`;
  }, [filteredPath, filters.searchText]);

  /**
   * Render unified surface with integrated filter panel and browser
   * The browser will navigate normally from the filtered starting point
   * Search text is included in the path as an S3 prefix filter
   */
  return (
    <Paper
      elevation={2}
      sx={{
        bgcolor: themeColors.paper,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Integrated Filter Panel */}
      <S3FilterPanel basePath={basePath} variant="integrated" />

      {/* File Browser */}
      <Box sx={{ p: 0 }}>
        <S3FileBrowser basePath={filteredPath} key={browserKey} />
      </Box>
    </Paper>
  );
};

export default FilteredS3Browser;
